/**
 * API client for admin Global-e migration and portal actions (migrate, activate, open RMA export).
 */
import { injectable } from "inversify";
import { TYPE, rootContainer } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import {
    IMigrateGlobalEMerchantsRequestData
} from "../../pages/admins/migrate-global-e-merchants/stores/migrate-global-e-merchants-page-store";

export enum GlobalEEnvironment {
    QA = "QA",
    INT = "INT",
    STG = "STG",
    PROD = "PRODUCTION",
}

export interface IMigrateGlobaleShopifyStoreRequest {
    portal_id: number;
}

export interface IMigrateGlobaleShopifyStoreResponse {
    num_deleted_reasons: number;
    num_deleted_resolutions: number;
    num_deleted_return_methods: number;
}

export interface IGlobaleStoreMetaFields {
    merchant_id: number;
    env: GlobalEEnvironment;
}

export interface ILastCompletedShopMigrationAttempt {
    migration_finished_at: string | null;
    status: "completed" | "completed_with_errors";
}

export interface IActivateGlobaleMerchantResponse {
    successful_activation: boolean;
}

export type DownloadOpenRmasResult =
    | { kind: "file"; blob: Blob; filename: string }
    | { kind: "message"; message: string };

function filenameFromContentDisposition(header: string | undefined, fallback: string): string {
    if (!header) {
        return fallback;
    }
    const star = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].trim());
        } catch {
            return star[1].trim();
        }
    }
    const quoted = /filename="([^"]+)"/i.exec(header);
    if (quoted?.[1]) {
        return quoted[1].trim();
    }
    const plain = /filename=([^;\n]+)/i.exec(header);
    if (plain?.[1]) {
        return plain[1].replace(/["']/g, "").trim();
    }
    return fallback;
}

@injectable()
class AdminMigrateGlobalEMerchantsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    public async migrateGlobalEMerchants(request: IMigrateGlobalEMerchantsRequestData): Promise<void> {
        await this.httpService.post<IApiResponse<void>>(
            "/rms-api/admin-migrate-global-e-merchants",
            {
              ...request
            }
        );
    }

    public async migrateGlobalEShopifyPlatformStore(request: IMigrateGlobaleShopifyStoreRequest): Promise<IMigrateGlobaleShopifyStoreResponse> {
        const res = await this.httpService.post<IApiResponse<IMigrateGlobaleShopifyStoreResponse>>(
            "/rms-api/admin-migrate-shopify-platform-globale-merchants",
            {
              ...request
            }
        );
        return res.payload;
    }
    
    public async fetchGlobaleStoreMetaFields(portalId: number): Promise<IGlobaleStoreMetaFields> {
        const res = await this.httpService.get<IApiResponse<IGlobaleStoreMetaFields>>(
            `/rms-api/admin-migrate-shopify-get-globale-metafields/${portalId}`
        );
        return res.payload;
    }

    public async fetchLastCompletedShopMigrationAttempt(
        portalId: number
    ): Promise<ILastCompletedShopMigrationAttempt | null> {
        const res = await this.httpService.get<
            IApiResponse<{ last_completed_attempt: ILastCompletedShopMigrationAttempt | null }>
        >(`/rms-api/admin-migrate-last-completed-migration-attempt/${portalId}`);
        return res.payload.last_completed_attempt;
    }

    public async activateGlobaleMerchant(request: IMigrateGlobaleShopifyStoreRequest): Promise<IActivateGlobaleMerchantResponse> {
        const res = await this.httpService.post<IApiResponse<IActivateGlobaleMerchantResponse>>(
            '/rms-api/admin-activate-globale-merchant',
            request
        );
        return res.payload;
    }

    /**
     * POST /rms-api/admin-download-open-rmas — CSV file or JSON body with `message` when no export is ready.
     */
    public async downloadOpenRmas(portalId: number): Promise<DownloadOpenRmasResult> {
        const response = await this.httpService.postDownload("/rms-api/admin-download-open-rmas", {
            portal_id: portalId,
        });

        const contentType = String(response.headers["content-type"] ?? "");
        if (contentType.includes("application/json")) {
            const text = await (response.data as Blob).text();
            const json = JSON.parse(text) as { message?: string };
            return { kind: "message", message: String(json.message ?? "") };
        }

        const disposition = response.headers["content-disposition"] as string | undefined;
        const filename = filenameFromContentDisposition(disposition, "open-rmas.csv");
        return { kind: "file", blob: response.data as Blob, filename };
    }
}

export default AdminMigrateGlobalEMerchantsService;
